import { FC } from 'react';
import { Outcome, Severity } from '@agilelab/plugin-wb-governance-common';
import {
  Box,
  BoxProps,
  createStyles,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { WbInfoTooltip } from '@agilelab/plugin-wb-platform';
import ErrorIcon from '@material-ui/icons/Error';
import TimelapseIcon from '@material-ui/icons/Timelapse';
import WarningIcon from '@material-ui/icons/WarningOutlined';
import {
  KoFilledIcon,
  NoResourcesFoundIcon,
  OkFilledIcon,
} from './outcomeIcons';

const useStyles = makeStyles(() =>
  createStyles({
    icon: {
      width: '1.3em',
      height: '1.3em',
    },
  }),
);

export type OutcomeIconProps = {
  outcome: Outcome | Severity;
} & BoxProps;

export const OutcomeIcon: FC<OutcomeIconProps> = ({ outcome, ...boxProps }) => {
  const classes = useStyles();
  const theme = useTheme();

  const outcomeIcon: Record<Outcome | Severity, JSX.Element> = {
    [Outcome.Ok]: (
      <OkFilledIcon
        htmlColor={theme.palette.success.main}
        className={classes.icon}
      />
    ),
    [Outcome.Error]: (
      <WbInfoTooltip
        title="Failed with severity Error. You need to solve the errors reported since they will prevent the deployment"
        icon={
          <KoFilledIcon
            htmlColor={theme.palette.error.main}
            className={classes.icon}
          />
        }
      />
    ),
    [Outcome.Info]: (
      <WbInfoTooltip
        title="Failed with severity Info. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail"
        icon={
          <ErrorIcon
            className={classes.icon}
            htmlColor={theme.palette.info.main}
          />
        }
      />
    ),
    [Outcome.Warning]: (
      <WbInfoTooltip
        title="Failed with severity Warning. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail"
        icon={
          <WarningIcon
            className={classes.icon}
            htmlColor={theme.palette.warning.main}
          />
        }
      />
    ),
    [Outcome.NotBlockingError]: (
      <WbInfoTooltip
        title="Failed. This policy is failed but it is not blocking the deployment. Check and solve the errors."
        icon={
          <ErrorIcon
            className={classes.icon}
            htmlColor={theme.palette.warning.main}
          />
        }
      />
    ),
    [Outcome.NotExecuted]: (
      <WbInfoTooltip
        title="Failed. You need to solve the errors reported since they will prevent the deployment"
        icon={
          <KoFilledIcon
            htmlColor={theme.palette.error.main}
            className={classes.icon}
          />
        }
      />
    ),
    [Outcome.NoResource]: (
      <WbInfoTooltip
        title="Failed with No resource. You need to solve the errors reported since they will prevent the deployment"
        icon={
          <NoResourcesFoundIcon
            className={classes.icon}
            htmlColor={theme.palette.error.main}
          />
        }
      />
    ),
    [Outcome.Pending]: (
      <WbInfoTooltip
        title="Pending."
        icon={
          <TimelapseIcon
            className={classes.icon}
            htmlColor={theme.palette.info.main}
          />
        }
      />
    ),
  };

  return (
    <Box
      display="flex"
      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
      {...boxProps}
    >
      {outcomeIcon[outcome]}
    </Box>
  );
};
